import { FC, useEffect, useState } from 'react'
import { IArpMedia } from '../../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../../features/arp-registry/ArpRegistry'
import { ArpMediaType } from '../../../types/common'
import { getSampleOpenseaLink } from '../../../utils/media'
import CenteredLoadingAnimation from '../../core/CenteredLoadingAnimation'
import { Column, Row } from '../../core/Components'
import { formatEpochTimestamp } from '../../core/helpers/dates'
import { prettyPrintWalletAddress } from '../../core/Utils'
import { formatTime } from '../../player/PlayerUtils'
import { InfoRow } from '../../profile/media-content/ProfileCurrentlyPlaying'
import {
  MediaExtraInfo,
  MediaInfoButtons,
  MediaInfoColumn,
  MediaInfoTitle
} from '../shared/MediaInfo'
import useMetatagParser, { SamplePacks } from '../shared/samplePackUtils'

export const useSampleInfo = ({ tokenId }: { tokenId: number }) => {
  const [openseaLink, setOpenseaLink] = useState<string>(null)
  const [data, setData] = useState<IArpMedia>(null)
  const [sampleDuration, setSampleDuration] = useState<number>()

  useEffect(() => {
    ArpRegistry.getByIdAsync(tokenId).then((sample) => {
      setData(sample)
      sample.fetchAudioBuffer().then((audioBuff) => setSampleDuration(audioBuff.duration))
    })
    getSampleOpenseaLink(tokenId).then((link) => setOpenseaLink(link))
  }, [tokenId])

  const createTime = data?.createdAt && parseInt(data.createdAt)
  const mintDate = createTime && formatEpochTimestamp(createTime, 'MM/DD/YY')

  return {
    sample: data,
    sampleLength: formatTime(sampleDuration),
    isReady: data !== null,
    mintDate,
    openseaLink
  }
}

interface Props {
  index: number
}
const SampleInfo: FC<Props> = ({ index }) => {
  const { sample, isReady, sampleLength, mintDate, openseaLink } = useSampleInfo({
    tokenId: index
  })

  const { meta } = useMetatagParser(index, 'sample')
  // Show the sample pack name if it's attached to the metadata
  const customSamplePackTitle = meta['sample-pack-name'] ? meta['sample-pack-name'] : undefined
  // If this sample pack is defined in our list, add the additional metadata from there.
  const customSamplePackDescription = SamplePacks[meta['sample-pack-name']]
    ? SamplePacks[meta['sample-pack-name']].description
    : undefined

  if (!isReady) {
    return <CenteredLoadingAnimation />
  }

  return (
    <Column css={{ width: '100%' }}>
      <MediaInfoTitle
        customSamplePackTitle={customSamplePackTitle}
        customSamplePackDescription={customSamplePackDescription}
        title={sample.metadata.title}
        artistEthAddress={sample.user.walletAddress}
        artistName={sample.user.displayName}
      />
      {sample.remixCount > 0 && (
        <MediaExtraInfo css={{ marginTop: '35px' }}>Times Used: {sample.remixCount}</MediaExtraInfo>
      )}
      <Row
        css={{
          fontSize: '14px',
          marginTop: !sample.remixCount && '50px',
          flexDirection: 'column',
          '@tablet': { flexDirection: 'row' }
        }}
      >
        <MediaInfoColumn title="Overview" css={{ flex: '1', '@tablet': { marginRight: '25px' } }}>
          <InfoRow title="Contract Address" tight>
            {prettyPrintWalletAddress(sample.contractAddress || '')}
          </InfoRow>
          <InfoRow title="Token ID" tight>
            {sample.tokenId}
          </InfoRow>
          <InfoRow title="Blockchain" tight>
            Polygon
          </InfoRow>
        </MediaInfoColumn>
        <MediaInfoColumn title="Sample properties" css={{ flex: '1' }}>
          <InfoRow title="Genre" tight>
            {sample.metadata.genres[0]}
          </InfoRow>
          <InfoRow title="Duration" tight>
            {sampleLength}
          </InfoRow>
          <InfoRow title="Mint date" tight>
            {mintDate}
          </InfoRow>
        </MediaInfoColumn>
      </Row>
      <MediaInfoButtons type={ArpMediaType.SAMPLE} index={index} openseaLink={openseaLink} />
    </Column>
  )
}

export default SampleInfo
